import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, CalendarDays, ArrowRight, Check } from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import ChatBot from "../components/ChatBot";
import FloatingCallButton from "../components/FloatingCallButton";
import SEOHead from "../components/SEOHead";

export default function Devis() {
  const navigate = useNavigate();

  const goToBooking = () => {
    // Naviguer à la homepage puis scroller vers #contact.
    // Re-scroll plusieurs fois : le contenu au-dessus (images) se charge
    // progressivement et décale la position de la section.
    navigate("/");
    [200, 700, 1500].forEach((delay) => {
      setTimeout(() => {
        document
          .getElementById("contact")
          ?.scrollIntoView({ behavior: delay === 200 ? "smooth" : "auto", block: "start" });
      }, delay);
    });
  };

  return (
    <div
      data-testid="devis-page"
      className="bg-[#050505] text-white min-h-screen"
    >
      <SEOHead
        title="Demande de devis — GMC Rénovation Façade Valenciennes"
        description="Réservez votre rendez-vous devis gratuit : un expert GMC se déplace chez vous pour établir un devis précis, sans engagement. Réponse sous 24h."
        path="/devis"
      />
      <Nav />
      <main className="pt-28 pb-24">
        <section className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-2xl mx-auto mb-16"
          >
            <div className="eyebrow mb-4 inline-flex items-center gap-2">
              <Sparkles size={12} />
              <span>Demande de devis · Gratuit sous 24h</span>
            </div>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-light leading-tight mb-4">
              Réservez votre
              <br />
              <em className="text-[#FF5A00] not-italic font-normal">
                rendez-vous devis gratuit
              </em>
            </h1>
            <p className="text-neutral-400 text-base md:text-lg leading-relaxed">
              Un expert GMC se déplace gratuitement chez vous pour établir un
              devis précis, sans engagement.
            </p>
          </motion.div>

          <div data-testid="devis-choices" className="flex justify-center">
            {/* Réservation RDV — parcours unique */}
            <motion.button
              type="button"
              onClick={goToBooking}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              data-testid="devis-choice-booking"
              className="group text-left relative overflow-hidden rounded-3xl border border-[#FF5A00]/40 bg-gradient-to-br from-[#FF5A00]/12 via-[#0a0a0a] to-[#0a0a0a] p-8 md:p-10 w-full max-w-xl transition hover:border-[#FF5A00]/70 hover:shadow-2xl hover:shadow-[#FF5A00]/10"
            >
              <div
                aria-hidden
                className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#FF5A00]/15 blur-3xl pointer-events-none"
              />
              <div className="relative">
                <div className="w-14 h-14 rounded-2xl bg-[#FF5A00]/15 border border-[#FF5A00]/30 flex items-center justify-center mb-5">
                  <CalendarDays size={22} className="text-[#FF5A00]" />
                </div>
                <div className="eyebrow mb-2">Visite d&apos;un expert · Gratuit</div>
                <h2 className="font-display text-2xl md:text-3xl text-white mb-3 leading-tight">
                  📅 Réserver un rendez-vous
                </h2>
                <p className="text-neutral-300 text-sm md:text-base leading-relaxed mb-6">
                  Décrivez votre projet, choisissez vos créneaux — un
                  conseiller GMC vous rappellera pour confirmer votre
                  rendez-vous à domicile.
                </p>
                <ul className="space-y-2 text-xs text-neutral-400 mb-8">
                  {[
                    "Formulaire en 5 étapes courtes",
                    "Choisissez vos disponibilités",
                    "Visite technique gratuite sur site",
                    "Devis précis remis sous 24h après la visite",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <Check size={13} className="text-[#FF5A00] mt-0.5 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="inline-flex items-center gap-2 text-[#FF5A00] font-medium text-sm">
                  Réserver un créneau
                  <ArrowRight
                    size={16}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </div>
              </div>
            </motion.button>
          </div>
        </section>
      </main>
      <Footer />
      <ChatBot />
      <FloatingCallButton />
    </div>
  );
}
